import {
  COMPANY_KEY_FACTS,
  type CompanyCardDto,
  type CompanyIdentity,
  type CompanyKeyFact,
  type CompanySource,
  type IdentityField,
  type SetupProgressDto,
} from '@bk/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant } from '../../../lib/useTenant';
import { Button, ErrorText } from '../../../ui';
import { LegalFormSelect } from '../Company';
import { useSetupProgress } from '../Setup';
import { StepIntro } from './Illustration';
import { DoneBand, Feeds, Row, RowGroup, useRows } from './Summary';

type Fact = CompanyKeyFact;

/** Ogni dato come una domanda che chiunque in azienda sa riconoscere. */
const ASK: Record<
  Fact,
  { domanda: string; manca: string; tipo: 'text' | 'number' | 'date' | 'forma' }
> = {
  ragione_sociale: {
    domanda: 'Come si chiama',
    manca: 'Come si chiama la tua azienda?',
    tipo: 'text',
  },
  in_breve: {
    domanda: 'Di cosa si occupa',
    manca: 'Di cosa si occupa, in poche parole?',
    tipo: 'text',
  },
  forma_giuridica: { domanda: 'Che società è', manca: 'Che tipo di società è?', tipo: 'forma' },
  addetti: {
    domanda: 'Quante persone ci lavorano',
    manca: 'Quante persone ci lavorano, più o meno?',
    tipo: 'number',
  },
  data_costituzione: { domanda: 'Da quando esiste', manca: 'Da quando esiste?', tipo: 'date' },
  pec: { domanda: 'La sua PEC', manca: 'Qual è la PEC dell’azienda?', tipo: 'text' },
};

const FROM: Record<CompanySource, string> = {
  visura: 'dalla visura',
  persona: 'detto da te',
  documento: 'da un tuo documento',
  posta: 'dalla posta',
  sito: 'dal vostro sito',
  registro: 'da una pagina pubblica',
  online: 'da una pagina online sulla sede',
  supposto: 'supposto dal cervello',
};

/** Da dove viene un dato: «dalla visura e dal vostro sito». */
function from(list: CompanySource[]): string {
  const parts = list.map((x) => FROM[x]);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} e ${parts.at(-1)}` : (parts[0] ?? '');
}

/** Gli altri dati di registro: si possono consultare, ma non si chiedono. */
const OTHER: Array<[IdentityField, string]> = [
  ['partita_iva', 'Partita IVA'],
  ['codice_fiscale', 'Codice fiscale'],
  ['rea', 'REA'],
  ['capitale_sociale', 'Capitale sociale'],
  ['stato_registro', 'Stato nel registro'],
  ['oggetto_sociale', 'Oggetto sociale'],
];

/** Un valore come lo legge una persona: la data all'italiana, sotto all'attività il codice ATECO. */
function shown(f: Fact, v: string | number, id: CompanyIdentity): { main: string; small?: string } {
  if (f === 'data_costituzione') {
    const d = new Date(String(v));
    return Number.isNaN(d.getTime())
      ? { main: String(v) }
      : { main: d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) };
  }
  if (f === 'addetti') return { main: `${v} ${Number(v) === 1 ? 'persona' : 'persone'}` };
  if (f === 'in_breve') {
    const code = /^[\d.]+/.exec(id.ateco_primario ?? '')?.[0];
    return { main: String(v), ...(code ? { small: `codice ATECO ${code}` } : {}) };
  }
  return { main: String(v) };
}

/**
 * Passo «L'impresa» della configurazione guidata, giocato come la visura: «è proprio la tua azienda?». Pochi dati
 * che chiunque riconosce, uno per scheda: un tocco per dire che è giusto, oppure lo correggi; se manca, te lo chiedo.
 */
export function CompanyStep({
  next,
  onNext,
}: {
  next: { titolo: string } | null;
  onNext: () => void;
}) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  // La scheda con ciò che si capisce dai dati: la forma giuridica tra quelle di legge, di cosa si occupa in parole semplici.
  const card = useQuery({
    queryKey: ['tenant', tenant.id, 'company', 'interpretata'],
    // Intanto la scheda già in memoria (anche quella della pagina Azienda): il passo non riparte da «Raccolgo…».
    placeholderData: () =>
      qc.getQueryData<CompanyCardDto>(['tenant', tenant.id, 'company', 'interpretata']) ??
      qc.getQueryData<CompanyCardDto>(['tenant', tenant.id, 'company']),
    staleTime: 30_000,
    // Un server che non sa ancora interpretare (o il modello che non risponde) non blocca il passo: la scheda com'è.
    queryFn: () =>
      api<CompanyCardDto>(t('/company/interpret'), { method: 'POST' }).catch(() =>
        api<CompanyCardDto>(t('/company')),
      ),
  });
  const progress = useSetupProgress();
  const conferme = progress.data?.guidata.conferme ?? {};
  const [review, setReview] = useState(false);
  const id = card.data?.identita;
  if (!id)
    return card.error ? (
      <div className="py-6 text-center">
        <ErrorText>{errorMessage(card.error)}</ErrorText>
        <Button variant="ghost" className="mt-2" onClick={() => void card.refetch()}>
          Riprova
        </Button>
      </div>
    ) : (
      <p className="py-6 text-center text-sm text-ink/50">Raccolgo i dati della tua azienda…</p>
    );
  const ok = (f: Fact) => id[f] != null && conferme[f] === String(id[f]);
  const done = COMPANY_KEY_FACTS.filter(ok).length;
  const all = done === COMPANY_KEY_FACTS.length;
  // Tutto confermato: il riepilogo al posto delle schede (ADR 0033). «Rivedi il passo» rimette le schede.
  const summary = all && !review;
  const other = OTHER.filter(([f]) => id[f] != null);
  return (
    <div className="pb-8 pt-2">
      <StepIntro step="impresa" title={summary ? 'La tua azienda' : 'È proprio la tua azienda?'}>
        <p className="mt-1 text-sm text-ink/60">
          {summary
            ? 'I dati che hai confermato. Per cambiarne uno tocca «Modifica»: si apre solo quello.'
            : 'Ecco cosa ho trovato. Dimmi se è giusto: un tocco per ogni riga, e se qualcosa non va lo correggi tu.'}
        </p>
        <div
          className={`mt-3 flex items-center gap-1.5 ${summary ? 'hidden' : ''}`}
          aria-label={`${done} di ${COMPANY_KEY_FACTS.length} confermati`}
        >
          {COMPANY_KEY_FACTS.map((f) => (
            <span
              key={f}
              className={`h-2 w-8 rounded-full transition-colors ${ok(f) ? 'bg-emerald-500' : 'bg-black/10'}`}
            />
          ))}
          <span className="ml-2 text-xs tabular-nums text-ink/55">
            {done} di {COMPANY_KEY_FACTS.length}
          </span>
        </div>
      </StepIntro>

      {summary ? (
        <div className="mt-6">
          <DoneBand title="La tua azienda è a posto" onReview={() => setReview(true)} />
          <CompanyFacts card={card.data!} confirmed={ok} />
          <Feeds steps={['Sedi', 'Attività economiche', 'Organizzazione e ruoli']} />
        </div>
      ) : (
        <>
          {review && all && (
            <p className="mt-4 text-center">
              <button
                className="text-sm text-accent hover:underline"
                onClick={() => setReview(false)}
              >
                ← Torna al riepilogo
              </button>
            </p>
          )}
          <ul className="mt-6 space-y-3">
            {COMPANY_KEY_FACTS.map((f) => (
              <FactCard key={f} f={f} card={card.data!} confirmed={ok(f)} />
            ))}
          </ul>
        </>
      )}

      {!!other.length && !summary && (
        <details className="group mt-5 text-sm">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-xs text-ink/50 [&::-webkit-details-marker]:hidden">
            <span className="transition-transform group-open:rotate-90">▶</span>
            Altri dati di registro (non devi fare nulla)
          </summary>
          <dl className="mt-2 grid gap-x-6 gap-y-1 pl-5 text-xs sm:grid-cols-2">
            {other.map(([f, label]) => (
              <div key={f} className={f === 'oggetto_sociale' ? 'sm:col-span-2' : ''}>
                <dt className="text-ink/45">{label}</dt>
                <dd className="text-ink/75">{String(id[f])}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span className="flex-1" />
        {next && (
          <Button onClick={onNext}>
            Avanti<span className="hidden sm:inline">: {next.titolo}</span> →
          </Button>
        )}
      </div>
    </div>
  );
}

export function FactCard({
  f,
  card,
  confirmed,
  edit = false,
  onDone,
}: {
  f: Fact;
  card: CompanyCardDto;
  confirmed: boolean;
  /** Si apre subito in modifica (dal riepilogo). */
  edit?: boolean;
  /** Salvato (true) o annullato: nel riepilogo la riga torna chiusa. */
  onDone?: (saved: boolean) => void;
}) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const v = card.identita[f] ?? null;
  const src = card.identita.fonti[f];
  const [editing, setEditing] = useState(edit);
  const [text, setText] = useState(edit && v !== null ? String(v) : '');
  const confirm = useMutation({
    mutationFn: async (value?: string) => {
      // Correggere vale anche come conferma: ciò che scrive la persona vale sopra le letture.
      if (value !== undefined)
        await api(t('/company/identity'), {
          method: 'PATCH',
          body: { [f]: ASK[f].tipo === 'number' ? Number(value) : value },
        });
      return api<SetupProgressDto['guidata']>(t('/onboarding/wizard'), {
        body: { conferma: { campo: f, valore: value ?? String(v) } },
      });
    },
    onSuccess: (guidata) => {
      qc.setQueryData<SetupProgressDto>(['tenant', tenant.id, 'onboarding-progress'], (old) =>
        old ? { ...old, guidata } : old,
      );
      setEditing(false);
      onDone?.(true);
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  const undo = useMutation({
    mutationFn: () =>
      api<SetupProgressDto['guidata']>(t('/onboarding/wizard'), { body: { annulla: f } }),
    onSuccess: (guidata) => {
      qc.setQueryData<SetupProgressDto>(['tenant', tenant.id, 'onboarding-progress'], (old) =>
        old ? { ...old, guidata } : old,
      );
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  const open = () => {
    setText(v === null ? '' : String(v));
    setEditing(true);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (value) confirm.mutate(ASK[f].tipo === 'number' ? String(Number(value)) : value);
  };
  const missing = v === null;
  const s = missing ? null : shown(f, v, card.identita);
  return (
    <li
      className={`rounded-2xl border-2 bg-white px-4 py-3 transition ${confirmed ? 'border-emerald-400/60' : missing ? 'border-dashed border-amber-300' : 'border-black/5'}`}
    >
      <p className="text-xs text-ink/50">{ASK[f].domanda}</p>
      {editing || missing ? (
        <form className="mt-1" onSubmit={submit}>
          {missing && !editing && <p className="mb-1 text-sm font-medium">{ASK[f].manca}</p>}
          <div className="flex flex-wrap gap-2">
            {ASK[f].tipo === 'forma' ? (
              <div className="min-w-0 flex-1">
                <LegalFormSelect value={text} onChange={setText} />
              </div>
            ) : (
              <input
                autoFocus={editing}
                type={ASK[f].tipo}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && editing) {
                    setEditing(false);
                    onDone?.(false);
                  }
                }}
                min={ASK[f].tipo === 'number' ? 0 : undefined}
                className="min-w-0 flex-1 rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
                placeholder={missing ? 'Scrivilo qui' : undefined}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            )}
            <Button type="submit" disabled={!text.trim() || confirm.isPending}>
              Salva
            </Button>
            {editing && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  onDone?.(false);
                }}
              >
                Annulla
              </Button>
            )}
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-base font-medium">{s!.main}</p>
            <p className="text-xs text-ink/40">
              {[s!.small, src ? from(src.da ?? [src.fonte]) : null].filter(Boolean).join(' · ')}
            </p>
          </div>
          {confirmed ? (
            <span className="flex items-center gap-3 text-sm">
              <span className="font-medium text-emerald-700">✓ Giusto</span>
              <button className="text-xs text-accent hover:underline" onClick={open}>
                cambia
              </button>
              <button
                className="text-xs text-ink/45 hover:text-ink/70 hover:underline disabled:opacity-50"
                disabled={undo.isPending}
                onClick={() => undo.mutate()}
              >
                annulla conferma
              </button>
            </span>
          ) : (
            <span className="flex gap-2">
              <button
                className="rounded-full bg-emerald-50 px-4 py-1.5 text-sm font-medium text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50"
                disabled={confirm.isPending}
                onClick={() => confirm.mutate(undefined)}
              >
                👍 Giusto
              </button>
              <button
                className="rounded-full bg-black/5 px-4 py-1.5 text-sm font-medium text-ink/70 transition hover:bg-black/10"
                onClick={open}
              >
                ✏️ Correggi
              </button>
            </span>
          )}
        </div>
      )}
      <ErrorText>
        {(confirm.error ?? undo.error) ? errorMessage(confirm.error ?? undo.error) : ''}
      </ErrorText>
    </li>
  );
}

/**
 * Di cosa si occupa l'impresa, dalle sue attività economiche (la principale per prima), quando il dato non c'è: con la
 * sola visura la frase in parole semplici non si scrive, ma le attività si conoscono già.
 */
export function whatItDoes(card: CompanyCardDto): { main: string; small?: string } | null {
  const acts = card.attivita
    .filter((a) => a.stato !== 'cessata')
    .sort((a, b) => Number(b.prevalente) - Number(a.prevalente));
  if (!acts.length) return null;
  const names = acts.map((a) => a.nome);
  const code = acts[0]!.ateco;
  return {
    main: names.length > 1 ? `${names.slice(0, -1).join(', ')} e ${names.at(-1)}` : names[0]!,
    small: [code ? `codice ATECO ${code}` : null, 'dalle attività economiche']
      .filter(Boolean)
      .join(' · '),
  };
}

const FACT_ICON: Record<Fact, string> = {
  ragione_sociale: '🏢',
  in_breve: '🏷️',
  forma_giuridica: '⚖️',
  addetti: '👥',
  data_costituzione: '📅',
  pec: '✉️',
};

/**
 * I dati dell'impresa nel riepilogo (ADR 0033), per «L'impresa» e per «Visura»: una riga per dato con «Modifica», che
 * apre il dato da solo e torna riga a salvataggio fatto; sotto, gli altri dati di registro da consultare.
 */
export function CompanyFacts({
  card,
  confirmed,
  manager = true,
}: {
  card: CompanyCardDto;
  confirmed: (f: Fact) => boolean;
  manager?: boolean;
}) {
  const rows = useRows();
  const id = card.identita;
  const other = OTHER.filter(([f]) => id[f] != null);
  return (
    <>
      <RowGroup title="Dati dell’impresa">
        {COMPANY_KEY_FACTS.map((f) => {
          const v = id[f];
          // Senza una frase su di cosa si occupa, valgono le attività economiche già note.
          const acts = f === 'in_breve' && v == null ? whatItDoes(card) : null;
          const s = v == null ? acts : shown(f, v, id);
          const src = acts ? null : id.fonti[f];
          return (
            <Row
              key={f}
              icon={FACT_ICON[f]}
              open={rows.open === f}
              edited={rows.edited === f}
              onEdit={manager ? () => rows.toggle(f) : null}
              editor={
                <ul>
                  <FactCard
                    f={f}
                    card={card}
                    confirmed={confirmed(f)}
                    edit
                    onDone={(saved) => rows.close(saved)}
                  />
                </ul>
              }
            >
              <p className="text-xs text-ink/50">{ASK[f].domanda}</p>
              <p className="font-medium">
                {s ? s.main : <span className="text-amber-700">Non lo so ancora</span>}
              </p>
              {s && (
                <p className="text-xs text-ink/40">
                  {[s.small, src ? from(src.da ?? [src.fonte]) : null].filter(Boolean).join(' · ')}
                </p>
              )}
            </Row>
          );
        })}
      </RowGroup>
      {!!other.length && (
        <RowGroup title="Dati di registro" sub="da consultare, non devi fare nulla">
          <li className="px-4 py-3">
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {other.map(([f, label]) => (
                <div key={f} className={f === 'oggetto_sociale' ? 'sm:col-span-2' : ''}>
                  <dt className="text-xs text-ink/45">{label}</dt>
                  <dd className="text-ink/80">{String(id[f])}</dd>
                </div>
              ))}
            </dl>
          </li>
        </RowGroup>
      )}
    </>
  );
}
